import { type FC } from 'react';
import { useSelector } from 'react-redux';
import { DiffMethod } from 'react-diff-viewer';

import { type ReorderConflict } from '@editor2/types/conflict';
import { type RootState } from '@editor2/reducers/root';
import ConflictDiffView from '../common/ConflictDiffView';
import { getChildName, getParentByEntityString } from '../helpers';

interface Props {
	conflict: ReorderConflict;
	courseId: number;
}

const InitialCurrentReorderView: FC<Props> = (props) => {
	const { conflict, courseId } = props;

	const courseState = useSelector((state: RootState) => state.courses[courseId]);

	const getInitNamesList = () => {
		const namesList = conflict.initial.map((id) => {
			const name = getChildName({ courseState, attribute: conflict.attribute, id });
			const isDeletedOrMoved = !conflict.current.includes(id);

			if (isDeletedOrMoved) {
				const parent = getParentByEntityString({
					state: courseState,
					entityString: conflict.attribute,
					childId: id
				});
				return parent ? `${name} [ moved to: ${parent.name} ]` : `${name} [ deleted ]`;
			} else {
				return name;
			}
		});

		return namesList.join('\n');
	};

	const getCurrentNamesList = () => {
		const namesList = conflict.current.map((id) => {
			const name = getChildName({ courseState, attribute: conflict.attribute, id });
			const isNewOrMoved = !conflict.initial.includes(id);

			if (isNewOrMoved) {
				const commonPayload = { entityString: conflict.attribute, childId: id };
				const initialParent = getParentByEntityString({
					...commonPayload,
					state: courseState.initialCourse
				});
				const currentParent = getParentByEntityString({
					...commonPayload,
					state: courseState
				});
				return initialParent ? `${name} [ moved from: ${currentParent.name} ]` : `${name} [ new ]`;
			} else {
				return name;
			}
		});

		return namesList.join('\n');
	};

	return (
		<ConflictDiffView
			oldValue={getInitNamesList()}
			newValue={getCurrentNamesList()}
			compareMethod={DiffMethod.LINES}
		/>
	);
};

export default InitialCurrentReorderView;
